import { getRandomIntInclusive } from '@bblabs/module-library/functions';
import Debug from 'debug';
type PollingServiceIntervals = {
  minute: number;
  rapid: number;
  long: number;
};

export abstract class PollingService {
  protected debug = Debug('o365:PollingService');
  protected readonly errorCountThreshold = 10;
  protected hasRunInitialPoll = false;
  protected intervals: PollingServiceIntervals = {
    minute: 1000 * 60, // 1 minute
    rapid: 1000 * 60 * 10, // 10 minutes
    long: 1000 * 60 * 60 * 6, // 6 hours
  };
  protected isPolling = false;
  protected _isPollRunning = false;
  protected pollingMode: keyof PollingServiceIntervals = 'rapid';
  protected subsequentErrorCount = 0;
  protected timeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Returns a promise that may or may not delay between 1-30 seconds.
   *
   * Note: does not delay on initial run.
   */
  protected async delay(maxDelay = 30): Promise<void> {
    return new Promise((resolve) => {
      // do not delay initial polling
      if (!this.hasRunInitialPoll) {
        return resolve();
      }

      // 50% chance that we delay at all
      const shouldDelay = getRandomIntInclusive(1, 10);
      if (shouldDelay % 2 === 0) {
        return resolve();
      }

      // delay between 1-30 seconds.
      setTimeout(resolve, getRandomIntInclusive(1, maxDelay) * 1000);
    });
  }

  protected abstract doPoll(): Promise<boolean>;

  protected incrementErrorCount() {
    this.subsequentErrorCount += 1;
    if (this.subsequentErrorCount > this.errorCountThreshold) {
      this.pollingMode = 'long';
    }
  }

  async poll() {
    // don't make a new set of requests if we are still running a request
    if (this.isPolling) {
      this.debug('Skipping poll() run because we are already polling');
      return;
    }

    this.isPolling = true;

    this.debug('starting doPoll()');
    const result = await this.doPoll();
    this.debug('finished doPoll()');
    if (result) {
      this.subsequentErrorCount = 0;
    }

    this.isPolling = false;
    this.setupNextTimeout();
    return result;
  }

  async startPolling() {
    this.debug('Start polling');
    this._isPollRunning = true;

    // if we haven't run a poll yet, do that immediately. poll() will
    // handle setting up the next timeout. If we have, we can just setup
    // the timeout directly.
    if (!this.hasRunInitialPoll) {
      await this.poll();
    } else {
      this.setupNextTimeout();
    }
  }

  /**
   * Turn off polling and reset flags/counters
   */
  stopPolling() {
    this.debug('Stop polling');
    this.pollingMode = 'rapid';
    this.hasRunInitialPoll = false;
    this.subsequentErrorCount = 0;
    this._isPollRunning = false;
    this.isPolling = false;
    this.cleanupTimeout();
  }

  protected cleanupTimeout() {
    this.timeout && clearTimeout(this.timeout);
    this.timeout = null;
  }

  /**
   * Schedule the next poll call.
   *
   * Using timeouts so we can vary the interval based on PollingMode.
   */
  protected setupNextTimeout() {
    this.cleanupTimeout();
    const nextTimeoutDate = Date.now() + this.intervals[this.pollingMode];
    this.debug(`Next polling timeout set [${this.pollingMode}]`, new Date(nextTimeoutDate));
    this.timeout = setTimeout(this.poll.bind(this), this.intervals[this.pollingMode]);
  }

  get isPollRunning() {
    return this._isPollRunning;
  }

  setPollingMode(pollingMode: keyof PollingServiceIntervals) {
    this.pollingMode = pollingMode;
    this.setupNextTimeout();
  }
}
